import { Stack, Typography } from "@mui/material";
import StatusChip from "@entrilia/shared/components/StatusChip";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { CompanyWithAccess } from "../definitions";
import InviteMembersContext from "./InviteMembersContext";

interface Props {
  company: CompanyWithAccess;
}

const CompanyEntities = ({ company }: Props) => {
  const { getCompanyEntities, entitiesFetchError } = useDefinedContext(InviteMembersContext);

  const entities = getCompanyEntities(company.clientCode);
  const entitiesPending = !company.accessToAllEntities && entities === undefined;
  const total = entities?.length ?? 0;
  const assignedEntities = (entities ?? [])
    .filter((entity) => company.entityIds.includes(entity.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const twoOrLessEntitiesToShow = assignedEntities.length > 2 ? assignedEntities.slice(0, 2) : assignedEntities;
  const allEntities = assignedEntities.length === total || company.accessToAllEntities;
  const totalsLabel = allEntities ? `${total}` : `${assignedEntities.length}/${total}`;

  return (
    <Stack direction="row" spacing={4} sx={{ justifyItems: "center", alignItems: "center", height: 3 }}>
      <Typography sx={{ width: "3rem" }} variant="subtitle2">
        Entities
      </Typography>
      {entitiesPending && (
        <Typography color="textSecondary">
          {entitiesFetchError ? "Entities could not be loaded." : "Loading entities..."}
        </Typography>
      )}
      {!entitiesPending && !assignedEntities.length && !company.accessToAllEntities && (
        <Typography>Set entities access. Access will be limited to specified entities.</Typography>
      )}
      {!entitiesPending && (
        <Stack direction="row" spacing={2.5} sx={{ justifyItems: "center", alignItems: "center" }}>
          {allEntities && <Typography>All Entities</Typography>}
          {!allEntities && <Typography>{twoOrLessEntitiesToShow.map((entity) => entity.name).join(";  ")}</Typography>}
          {!company.accessToAllEntities && (
            <StatusChip label={totalsLabel} color={(theme) => theme.palette.text.secondary} />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default CompanyEntities;
